import { OpenFeatureEventEmitter, ProviderEvents, TypeMismatchError } from '@openfeature/web-sdk';
import { createFlagsmithInstance } from 'flagsmith';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * Ret a value of the specified type based on the type parameter.
 *
 * @param value - The value to be converted or validated.
 * @param type - The target type for the conversion.
 * @returns The converted value if successful, or null if conversion fails or the type is unsupported.
 */
const typeFactory = (value, type) => {
    if (value === null)
        return undefined;
    switch (type) {
        case 'string':
            return value !== null && typeof value !== 'undefined' ? `${value}` : value;
        case 'number':
            return typeof value === 'number' ? value : parseFloat(value) || value;
        case 'boolean':
            return typeof value === 'boolean' ? value : false;
        case 'object':
            if (typeof value === 'string') {
                try {
                    return JSON.parse(value);
                }
                catch (error) {
                    return value;
                }
            }
            return value;
        default:
            return value;
    }
};

class FlagsmithClientProvider {
    constructor(_a) {
        var { logger, flagsmithInstance } = _a, config = __rest(_a, ["logger", "flagsmithInstance"]);
        this.metadata = {
            name: FlagsmithClientProvider.name,
        };
        this.runsOn = 'client';
        // The Open Feature event emitter
        this.events = new OpenFeatureEventEmitter();
        this._logger = logger;
        this._client = flagsmithInstance || createFlagsmithInstance();
        this._config = config;
    }
    initialize(context) {
        return __awaiter(this, undefined, undefined, function* () {
            var _a;
            const identity = context === null || context === undefined ? undefined : context.targetingKey;
            if ((_a = this._client) === null || _a === undefined ? undefined : _a.initialised) {
                //Already initialised, set the state based on the new context, allow certain context props to be optional
                const defaultState = Object.assign(Object.assign({}, this._client.getState()), { identity: undefined, traits: {} });
                const isLogout = !!this._client.identity && !identity;
                this._client.identity = identity;
                this._client.setState(Object.assign(Object.assign({}, defaultState), (context || {})));
                this.events.emit(ProviderEvents.Stale, { message: 'context has changed' });
                return isLogout ? this._client.logout() : this._client.getFlags();
            }
            const serverState = this._config.state;
            if (serverState) {
                this._client.setState(serverState);
                this.events.emit(ProviderEvents.Ready, { message: 'flags provided by SSR state' });
            }
            return this._client.init(Object.assign(Object.assign(Object.assign({}, this._config), context), { identity, onChange: (previousFlags, params, loadingState) => {
                    var _a, _b;
                    const eventMeta = {
                        metadata: this.getMetadata(),
                        flagsChanged: params.flagsChanged,
                    };
                    this.events.emit(ProviderEvents.Ready, Object.assign({ message: 'Flags ready' }, eventMeta));
                    if (params.flagsChanged) {
                        this.events.emit(ProviderEvents.ConfigurationChanged, Object.assign({ message: 'Flags changed' }, eventMeta));
                    }
                    (_b = (_a = this._config).onChange) === null || _b === undefined ? undefined : _b.call(_a, previousFlags, params, loadingState);
                } }));
        });
    }
    onContextChange(oldContext, newContext) {
        this.events.emit(ProviderEvents.Stale, { message: 'Context Changed' });
        return this.initialize(newContext);
    }
    resolveBooleanEvaluation(flagKey) {
        return this.evaluate(flagKey, 'boolean', false);
    }
    resolveStringEvaluation(flagKey, defaultValue) {
        return this.evaluate(flagKey, 'string', defaultValue);
    }
    resolveNumberEvaluation(flagKey, defaultValue) {
        return this.evaluate(flagKey, 'number', defaultValue);
    }
    resolveObjectEvaluation(flagKey, defaultValue) {
        return this.evaluate(flagKey, 'object', defaultValue);
    }
    /**
     * Based on Flagsmith's state, return flag metadata
     * @private
     */
    getMetadata() {
        return Object.assign({ targetingKey: this._client.identity || '' }, (this._client.getAllTraits() || {}));
    }
    /**
     * Based on Flagsmith's loading state, determine the Open Feature resolution reason
     * @private
     */
    evaluate(flagKey, type, defaultValue) {
        const value = typeFactory(type === 'boolean' ? this._client.hasFeature(flagKey) : this._client.getValue(flagKey), type);
        if (typeof value !== 'undefined' && typeof value !== type) {
            throw new TypeMismatchError(`flag key ${flagKey} is not of type ${type}`);
        }
        return {
            value: (typeof value !== type ? defaultValue : value),
            reason: this.parseReason(value),
        };
    }
    /**
     * Based on Flagsmith's loading state and feature resolution, determine the Open Feature resolution reason
     * @private
     */
    parseReason(value) {
        var _a;
        if (value === undefined) {
            return 'DEFAULT';
        }
        switch ((_a = this._client.loadingState) === null || _a === undefined ? undefined : _a.source) {
            case 'CACHE':
                return 'CACHED';
            case 'DEFAULT_FLAGS':
                return 'DEFAULT';
            default:
                return 'STATIC';
        }
    }
    get flagsmithClient() {
        return this._client;
    }
}

export { FlagsmithClientProvider };
